import { Component, Input, OnInit, OnChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { forkJoin, of } from 'rxjs';
import { map } from 'rxjs/operators';

import { DashboardService } from '../board.service';
import { BoardConfigService } from '../board-config.service';
import {
  BoardConfig, BoardFilter, CardConfig,
  CardTransform, StatMapping,
} from '../board-config.model';

import { EchartCardComponent, EChartCardConfig } from '../echart-card/echart-card.component';
import { ChartjsCardComponent, ChartJsCardConfig } from '../chartjs-card/chartjs-card.component';
import { DarkStatsCardComponent, DarkStatsConfig } from '../dark-stats-card/dark-stats-card.component';

// ── Tipos internos de render ──────────────────────────────────────────────────

export type RenderedCard =
  | { kind: 'dark-stats'; cfg: DarkStatsConfig;   layout: string }
  | { kind: 'echart';     cfg: EChartCardConfig;  layout: string }
  | { kind: 'chartjs';    cfg: ChartJsCardConfig; layout: string };

export interface RenderedSection {
  id:      string;
  title?:  string;
  library?: string;
  cards:   RenderedCard[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function applyTransform(
  raw: Record<string, any>[],
  t: CardTransform,
): Record<string, any>[] {
  if (t.type === 'group_by_day') {
    const dayMap: Record<number, number> = {};
    raw.forEach(r => {
      const d = Number(r[t.dayField!]);
      dayMap[d] = (dayMap[d] ?? 0) + Number(r[t.sumField!]);
    });
    return Object.entries(dayMap)
      .sort(([a], [b]) => Number(a) - Number(b))
      .map(([day, val]) => ({
        [t.labelAs ?? 'dia']:   `Día ${day}`,
        [t.valueAs ?? 'value']: val,
      }));
  }
  return raw;
}

function buildSdvParam(sdv: string): object[] {
  return [{
    id: 'sdv',
    type: 'string/contains',
    value: [sdv],
    target: ['dimension', ['template-tag', 'sdv']],
    options: { 'case-sensitive': false },
  }];
}

function formatStatValue(
  raw: Record<string, any>,
  stat: StatMapping,
  filterKey?: string,
  filterVal?: string,
): string {
  const v = raw[stat.field];
  if (v === undefined || v === null) return '—';
  const n = Number(v);
  switch (stat.format) {
    case 'number':   return isNaN(n) ? String(v) : n.toLocaleString('es-CO');
    case 'percent':  return String(v); // ya viene como "58.9 %" de Metabase
    case 'currency': return isNaN(n) ? String(v) : '$ ' + n.toLocaleString('es-CO');
    default:         return String(v);
  }
}

// ─────────────────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-dynamic-dashboard',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    MatFormFieldModule, MatSelectModule,
    EchartCardComponent,
    ChartjsCardComponent, DarkStatsCardComponent,
  ],
  templateUrl: './dynamic-dashboard.component.html',
  styleUrl:    './dynamic-dashboard.component.scss',
})
export class DynamicDashboardComponent implements OnInit, OnChanges {

  @Input() boardId = 'sdv-recognition';

  private readonly configSvc  = inject(BoardConfigService);
  private readonly metabaseSvc = inject(DashboardService);

  // ── Estado ────────────────────────────────────────────────────────────────
  boardConfig:  BoardConfig | null = null;
  sections:     RenderedSection[]  = [];
  filterValues: Record<string, string> = {};   // key → valor seleccionado
  filterOptions: Record<string, string[]> = {}; // key → opciones (estáticas o desde card)

  loading = false;
  error   = false;

  // Caché de datos crudos por cardId (para filtros cliente)
  private rawCache: Record<number, Record<string, any>[]> = {};

  // ─────────────────────────────────────────────────────────────────────────
  ngOnInit(): void  { this.init(); }
  ngOnChanges(): void { this.init(); }

  private init(): void {
    this.loading = true;
    this.error   = false;
    this.configSvc.getConfig(this.boardId).subscribe({
      next: cfg => {
        this.boardConfig = cfg;
        this.initFilters(cfg);
        this.loadAllData();
      },
      error: () => { this.loading = false; this.error = true; },
    });
  }

  // ── Inicializar filtros con valores por defecto ───────────────────────────
  private initFilters(cfg: BoardConfig): void {
    cfg.filters.forEach(f => {
      if (!(f.key in this.filterValues)) {
        this.filterValues[f.key] = f.source === 'static'
          ? (f.options?.[0] ?? '')
          : 'all';
      }
      if (f.source === 'static' && f.options) {
        this.filterOptions[f.key] = f.options;
      }
    });
  }

  // ── Cuando el usuario cambia un filtro ───────────────────────────────────
  onFilterChange(filterKey: string, value: string): void {
    this.filterValues[filterKey] = value;

    const filter = this.boardConfig?.filters.find(f => f.key === filterKey);

    if (filter?.filterMode === 'client' || filter?.source === 'from_card') {
      // Solo re-renderizar con los datos ya cacheados
      this.renderSections();
    } else {
      // Filtro de servidor → recargar todo
      this.rawCache = {};
      this.loadAllData();
    }
  }

  // ── Cargar todos los cardIds únicos en paralelo ───────────────────────────
  private loadAllData(): void {
    if (!this.boardConfig) return;
    this.loading = true;

    // Recopilar todos los metabaseCardId únicos de todas las cards
    const allCards = this.boardConfig.sections.flatMap(s => s.cards);
    const uniqueIds = [...new Set(allCards.map(c => c.metabaseCardId))];

    // Construir parámetros según los filtros que cada card necesita
    // (simplificado: usamos el primer filtro de tipo server del board)
    const sdvFilter = this.boardConfig.filters.find(f => f.metabaseTemplateTag === 'sdv');
    const sdvValue  = sdvFilter ? this.filterValues[sdvFilter.key] : '';
    const sdvParams = sdvValue ? buildSdvParam(sdvValue) : [];

    const requests = uniqueIds.reduce((acc, id) => {
      acc[id] = this.metabaseSvc.getMetabaseCardJson(id, sdvParams).pipe(
        map(data => data as Record<string, any>[]),
      );
      return acc;
    }, {} as Record<number, any>);

    forkJoin(requests).subscribe({
      next: results => {
        this.rawCache = results as Record<number, Record<string, any>[]>;

        // Poblar opciones de filtros que vienen de un card
        this.boardConfig!.filters
          .filter(f => f.source === 'from_card')
          .forEach(f => {
            const raw = this.rawCache[f.sourceCardId!] ?? [];
            const opts = [...new Set(raw.map(r => String(r[f.sourceField!])))].sort();
            this.filterOptions[f.key] = opts;
            // Si el valor actual no existe ya, resetear a 'all'
            if (this.filterValues[f.key] !== 'all' && !opts.includes(this.filterValues[f.key])) {
              this.filterValues[f.key] = 'all';
            }
          });

        this.renderSections();
        this.loading = false;
      },
      error: () => { this.loading = false; this.error = true; },
    });
  }

  // ── Convertir config + datos crudos → RenderedSection[] ─────────────────
  private renderSections(): void {
    if (!this.boardConfig) return;

    this.sections = this.boardConfig.sections.map(section => ({
      id:      section.id,
      title:   section.title,
      library: section.library,
      cards:   section.cards
        .map(card => this.renderCard(card))
        .filter((c): c is RenderedCard => c !== null),
    }));
  }

  private renderCard(card: CardConfig): RenderedCard | null {
    let raw = this.rawCache[card.metabaseCardId] ?? [];

    // Aplicar transformación si existe
    if (card.transform) {
      raw = applyTransform(raw, card.transform);
    }

    // Aplicar maxRows
    if (card.maxRows) {
      raw = raw.slice(0, card.maxRows);
    }

    // Aplicar filtro cliente
    if (card.clientFilter) {
      const filterVal = this.filterValues[card.clientFilter.filterKey];
      if (filterVal && filterVal !== 'all') {
        raw = raw.filter(r => String(r[card.clientFilter!.field]) === filterVal);
      }
    }

    const layout = card.layout;

    // ── dark-stats ──────────────────────────────────────────────────────────
    if (card.chartType === 'dark-stats') {
      const firstRow = raw[0] ?? {};
      const stats = (card.stats ?? []).map(s => ({
        label:    s.label,
        value:    formatStatValue(firstRow, s),
        sublabel: s.sublabel,
      }));
      return {
        kind: 'dark-stats',
        layout,
        cfg: { title: card.title, cols: card.cols ?? 3, stats },
      };
    }

    // ── ECharts ─────────────────────────────────────────────────────────────
    if (card.chartType.startsWith('ec_')) {
      return {
        kind: 'echart',
        layout,
        cfg: {
          title:     card.title,
          chartType: card.chartType as any,
          labelKey:  card.labelKey,
          valueKey:  card.valueKey,
          value2Key: card.value2Key,
          data:      raw,
        } as EChartCardConfig,
      };
    }

    // ── Chart.js ────────────────────────────────────────────────────────────
    if (card.chartType.startsWith('cj_')) {
      return {
        kind: 'chartjs',
        layout,
        cfg: {
          title:     card.title,
          chartType: card.chartType as any,
          labelKey:  card.labelKey,
          valueKey:  card.valueKey,
          value2Key: card.value2Key,
          data:      raw,
        } as ChartJsCardConfig,
      };
    }

    return null;
  }

  // ── Helpers para la plantilla ────────────────────────────────────────────
  getFilterDef(key: string): BoardFilter | undefined {
    return this.boardConfig?.filters.find(f => f.key === key);
  }

  trackSection(_: number, s: RenderedSection): string { return s.id; }
  trackCard(_: number, c: RenderedCard): string { return (c.cfg as any).title ?? ''; }
}
